/**
 * ResultsDraftEditor — modal for an admin to review + edit the
 * auto-generated results email before publishing to all shareholders.
 *
 * Used by MeetingDetailScreen (opens when status === 'ended').
 *
 * Auto-generated content includes meeting name, attendance breakdown,
 * voting item results table, optional recording link, optional minutes.
 * Admin can edit subject / HTML body / plaintext body before publish.
 *
 * Two tabs: "Preview" renders the HTML for review (white email canvas),
 * "Edit" exposes subject + raw HTML + plaintext fallback as plain
 * <textarea>s. The legacy view edited the HTML body in a plain textarea
 * too (no rich-text editor / draft-js), so nothing is downgraded here —
 * the same fields + behavior are preserved exactly.
 */
import { useEffect, useState } from 'react';
import { X, Eye, Edit2, type LucideIcon } from 'lucide-react';
import { useResultsDraft, useUpdateResultsDraft } from '@/hooks/useBoard';

export function ResultsDraftEditor({
  meetingId,
  onClose
}: {
  meetingId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useResultsDraft(meetingId);
  const updateDraft = useUpdateResultsDraft(meetingId);

  const [tab, setTab] = useState<'preview' | 'edit'>('preview');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [textBody, setTextBody] = useState('');
  const [pristine, setPristine] = useState(true);

  useEffect(() => {
    if (!data) return;
    const draft = data.draft;
    if (draft) {
      setSubject(draft.subject || '');
      setHtml(draft.html || '');
      setTextBody(draft.text || '');
    }
  }, [data]);

  const onSave = async () => {
    await updateDraft.mutateAsync({ subject, html, text: textBody });
    setPristine(true);
  };

  const editedAt = data?.draft?.editedAt;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-nurev-border bg-nurev-surface-lift"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-nurev-border p-5">
          <div>
            <h2 className="text-lg font-semibold text-nurev-text">
              Review results email
            </h2>
            <div className="mt-0.5 text-[12px] text-nurev-muted">
              This is what will be sent to <strong>all shareholders</strong>{' '}
              when you publish. Edit as needed.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-nurev-muted hover:text-nurev-text"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-nurev-border px-5 pt-3">
          <TabBtn
            label="Preview"
            icon={Eye}
            active={tab === 'preview'}
            onClick={() => setTab('preview')}
          />
          <TabBtn
            label="Edit"
            icon={Edit2}
            active={tab === 'edit'}
            onClick={() => setTab('edit')}
          />
        </div>

        {/* Content */}
        <div className="flex-1 space-y-3 overflow-auto p-5">
          {isLoading && (
            <div className="py-8 text-center text-sm text-nurev-muted">
              Loading draft…
            </div>
          )}

          {!isLoading && tab === 'preview' && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-nurev-muted">
                Subject
              </div>
              <div className="border-b border-nurev-border pb-3 text-sm font-semibold text-nurev-text">
                {subject || '(empty)'}
              </div>
              <div
                className="rounded-lg bg-white p-5 text-sm text-black"
                style={{ minHeight: 200 }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: html || '<i>(empty body)</i>'
                }}
              />
            </>
          )}

          {!isLoading && tab === 'edit' && (
            <>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => {
                    setSubject(e.target.value);
                    setPristine(false);
                  }}
                  className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 text-sm text-nurev-text outline-none focus:border-amber-500/50"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                  HTML body
                </label>
                <textarea
                  value={html}
                  onChange={e => {
                    setHtml(e.target.value);
                    setPristine(false);
                  }}
                  rows={20}
                  className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 font-mono text-[12px] text-nurev-text outline-none focus:border-amber-500/50"
                />
                <div className="mt-1 text-[11px] text-nurev-muted">
                  HTML allowed. Inline styles are preserved. Switch to the
                  Preview tab to see how it looks.
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                  Plaintext fallback
                </label>
                <textarea
                  value={textBody}
                  onChange={e => {
                    setTextBody(e.target.value);
                    setPristine(false);
                  }}
                  rows={4}
                  className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3.5 py-2.5 text-[12px] text-nurev-text outline-none focus:border-amber-500/50"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-nurev-border p-5">
          <div className="text-[11px] text-nurev-muted">
            {pristine ? (
              editedAt ? (
                <>Edited {new Date(editedAt).toLocaleString()}</>
              ) : (
                <>Auto-generated · unedited</>
              )
            ) : (
              <strong className="text-amber-300">Unsaved changes</strong>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-nurev-border bg-white/[0.05] px-4 py-2 text-xs text-nurev-text"
            >
              Close
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={pristine || updateDraft.isPending}
              className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-nurev-bg transition hover:bg-amber-400 disabled:opacity-50"
            >
              {updateDraft.isPending ? 'Saving…' : 'Save draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  label,
  icon: Icon,
  active,
  onClick
}: {
  label: string;
  icon: LucideIcon;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? 'border-amber-500 text-amber-300'
          : 'border-transparent text-nurev-muted'
      }`}
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}
